import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../api';
import { useDocumentTitle } from '../lib/useDocumentTitle';

type Status = 'idle' | 'submitting' | 'error';

export function ResetPassword() {
  useDocumentTitle('Choose a new password - Neomaris');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  // No token in the URL → the link is incomplete; send the user to request
  // a fresh one rather than letting them fill in a form that can't submit.
  if (token === null || token.length === 0) {
    return (
      <section className="auth-panel">
        <div className="auth-card auth-card-v2">
          <div className="auth-state">
            <div className="auth-state-icon auth-state-icon--neutral">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 11v5" strokeLinecap="round" />
                <circle cx="12" cy="8" r="0.6" fill="currentColor" stroke="none" />
              </svg>
            </div>
            <h2>Let’s get you a fresh link</h2>
            <p className="auth-sub" role="status" data-testid="reset-invalid">
              This reset link has expired or is incomplete — that’s normal, they only last 60
              minutes. Request a new one and we’ll send it right over.
            </p>
            <Link to="/forgot-password" className="btn btn-primary auth-submit">
              Request a new link
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const canSubmit =
    status !== 'submitting' && password.length >= 8 && confirm.length > 0 && password === confirm;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (password !== confirm) {
      setError('The two passwords don’t match.');
      return;
    }
    setStatus('submitting');
    setError(null);
    try {
      await api('/v1/auth/password-reset/confirm', { method: 'POST', body: { token, password } });
      // Success — hand off to login with a one-shot banner. The user
      // re-authenticates with the new password, which also confirms it took.
      navigate('/login?reset=success', { replace: true });
    } catch (err) {
      const e = err as { body?: { error?: string; message?: string }; message?: string };
      setStatus('error');
      setError(
        e.body?.message ??
          e.message ??
          'Couldn’t reset your password. Request a new link and retry.'
      );
    }
  }

  return (
    <section className="auth-panel">
      <div className="auth-card auth-card-v2">
        <h2>Choose a new password</h2>
        <p className="auth-sub">Enter a new password for your account.</p>
        <form onSubmit={onSubmit} data-testid="reset-form">
          {error !== null ? (
            <div className="auth-banner auth-banner--error" role="alert">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 9v4" strokeLinecap="round" />
                <path
                  d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <ul className="form-errors" data-testid="error-banner">
                <li>{error}</li>
              </ul>
            </div>
          ) : null}
          <div className="form-group">
            <div className="field-password">
              <label htmlFor="reset-password">New password</label>
              <input
                type={showPw ? 'text' : 'password'}
                id="reset-password"
                className="form-input"
                aria-describedby="reset-password-req"
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="button" className="pw-toggle" onClick={() => setShowPw((v) => !v)}>
                <span className="sr-only">
                  {showPw ? 'Hide new password' : 'Show new password'}
                </span>
                {showPw ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path d="M3 3l18 18" strokeLinecap="round" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" strokeLinecap="round" />
                    <path
                      d="M9.4 5.2A9.6 9.6 0 0 1 12 5c5 0 9 4.5 9 7 0 1-1 2.6-2.7 4M6.3 6.3C3.9 7.8 3 10 3 12c0 1.4 1.9 4 5 5.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            <div className="pw-hints">
              <span
                id="reset-password-req"
                className={`pw-hint ${password.length >= 8 ? 'pw-hint--met' : ''}`}
              >
                {password.length >= 8 ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    aria-hidden="true"
                  >
                    <path d="M5 12l4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : null}
                At least 8 characters
              </span>
            </div>
          </div>
          <div className="form-group">
            <div className="field-password">
              <label htmlFor="reset-confirm">Confirm new password</label>
              <input
                type={showConfirm ? 'text' : 'password'}
                id="reset-confirm"
                className="form-input"
                autoComplete="new-password"
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
              <button type="button" className="pw-toggle" onClick={() => setShowConfirm((v) => !v)}>
                <span className="sr-only">
                  {showConfirm ? 'Hide password confirmation' : 'Show password confirmation'}
                </span>
                {showConfirm ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path d="M3 3l18 18" strokeLinecap="round" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" strokeLinecap="round" />
                    <path
                      d="M9.4 5.2A9.6 9.6 0 0 1 12 5c5 0 9 4.5 9 7 0 1-1 2.6-2.7 4M6.3 6.3C3.9 7.8 3 10 3 12c0 1.4 1.9 4 5 5.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {confirm.length > 0 ? (
              <div className="pw-hints">
                {password === confirm ? (
                  <span className="pw-hint pw-hint--met">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      aria-hidden="true"
                    >
                      <path d="M5 12l4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Passwords match
                  </span>
                ) : (
                  <span className="pw-hint pw-hint--pending">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      aria-hidden="true"
                    >
                      <path d="M6 12h12" strokeLinecap="round" />
                    </svg>
                    Passwords don’t match yet
                  </span>
                )}
              </div>
            ) : null}
          </div>
          <button type="submit" className="btn btn-primary auth-submit" disabled={!canSubmit}>
            {status === 'submitting' ? 'Saving…' : 'Set new password'}
          </button>
        </form>
        <p className="auth-foot">
          <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </section>
  );
}
